/* eslint-disable @typescript-eslint/no-non-null-assertion */

import SlButton from "@shoelace-style/shoelace/dist/components/button/button";
import SlDialog from "@shoelace-style/shoelace/dist/components/dialog/dialog";
import SlIcon from "@shoelace-style/shoelace/dist/components/icon/icon";
import { addStyles } from "@tedivo/tedivo-form";
import { removeChildren } from "@tedivo/tedivo-dom-helpers";

const styles = `
   sl-dialog::part(body) {
    overflow: var(--dialog-body-overflow, auto);
    width: 100%;
  }
`;

export class IntegratedDialog<IActions = string> {
  private internalDialog: SlDialog | undefined;
  public buttonsExtraNode: HTMLElement | undefined;
  public headerActionsExtraNode: HTMLElement | undefined;

  private htmlContent: HTMLDivElement | undefined;

  private buttons: SlButton[] = [];
  private nameStr = "";

  private options: IIntegratedDialogProps;

  get name() {
    return this.nameStr;
  }
  set name(v: string) {
    this.nameStr = v;
    if (this.internalDialog) {
      this.internalDialog.setAttribute("name", v);
    }
  }

  public onButtonClicked:
    | ((v: IActions, btn: SlButton) => boolean | Promise<boolean> | void)
    | undefined = undefined;

  public onDismissed: (() => void | Promise<void> | void) | undefined =
    undefined;

  constructor(options: IIntegratedDialogProps) {
    this.options = options;
  }

  updateOptions(options: Partial<IIntegratedDialogProps>) {
    this.options = { ...this.options, ...options };
    return this;
  }

  get isOpen() {
    return this.internalDialog?.open || false;
  }

  show(title: string, message: string | HTMLElement): this {
    const { dialog } = this.createDialog();
    dialog.label = title;

    const btnActionToFocus = this.options.buttonsAttrs?.find(
      (btnAttrs) => btnAttrs.autofocus,
    )?.value;

    if (btnActionToFocus) {
      const btn = this.buttons.find(
        (f) => f.dataset["action"] === btnActionToFocus,
      );
      if (btn) {
        btn.setAttribute("autofocus", "true");
      }
    }

    if (typeof message === "string") {
      this.htmlContent!.innerHTML = message;
    } else {
      removeChildren(this.htmlContent!);
      this.htmlContent!.appendChild(message);
    }

    dialog.show();
    return this;
  }

  setButtonEnabled(action: IActions, enabled: boolean) {
    const btn = this.buttons.find((f) => f.dataset["action"] === action);
    if (btn) btn.disabled = !enabled;
  }

  getButton(action: IActions) {
    return this.buttons.find((f) => f.dataset["action"] === action);
  }

  private createDialog(): {
    dialog: SlDialog;
  } {
    if (this.internalDialog) {
      return { dialog: this.internalDialog };
    }

    const dialog = document.createElement("sl-dialog");
    dialog.appendChild(addStyles(styles));
    if (this.nameStr) dialog.setAttribute("name", this.nameStr);

    dialog.className = "integrated-dialog";

    this.htmlContent = document.createElement("div");

    const buttonsHolder = document.createElement("div");
    buttonsHolder.className = "dialog-buttons-holder";
    buttonsHolder.slot = "footer";
    if (this.buttonsExtraNode) {
      buttonsHolder.appendChild(this.buttonsExtraNode);
    }

    if (this.headerActionsExtraNode) {
      this.headerActionsExtraNode.slot = "header-actions";
      dialog.appendChild(this.headerActionsExtraNode);
    }

    if (this.options.width) {
      dialog.style.setProperty("--width", this.options.width);
    }

    if (this.options.maxWidth) {
      dialog.style.setProperty("--dialog-max-width:", this.options.maxWidth);
    }

    if (this.options.bodyOverflow) {
      dialog.style.setProperty(
        "--dialog-body-overflow",
        this.options.bodyOverflow,
      );
    }

    const onClicked = async (value: IActions, btn: SlButton) => {
      if (this.onButtonClicked) {
        const res = await this.onButtonClicked(value, btn);
        if (res === false) return;
      }

      dialog.hide();
    };

    this.options.buttonsAttrs?.forEach((btnAttrs) => {
      const btn = document.createElement("sl-button");
      btn.variant = btnAttrs.variant || "primary";
      btn.outline = btnAttrs.outlined || false;
      btn.tabIndex = 0;
      btn.type = btnAttrs.type || "button";
      btn.dataset["action"] = btnAttrs.value;
      btn.innerHTML = btnAttrs.label;

      btn.addEventListener(
        "click",
        () => onClicked(btnAttrs.value as unknown as IActions, btn),
        false,
      );
      buttonsHolder.appendChild(btn);
      this.buttons.push(btn);

      if (btnAttrs.icon) {
        const icon = document.createElement("sl-icon") as SlIcon;
        icon.name = btnAttrs.icon;
        icon.slot = "prefix";
        btn.appendChild(icon);
      }
    });

    dialog.appendChild(this.htmlContent);
    dialog.appendChild(buttonsHolder);

    this.options.parentNode.appendChild(dialog);

    dialog.addEventListener("sl-request-close", (event: Event) => {
      if (event.target !== dialog) return;
      if (
        this.options.preventClose ||
        (this.options.preventDefaultClose &&
          (event as CustomEvent).detail.source === "overlay")
      ) {
        event.preventDefault();
      }
    });

    if (this.options.removeDialogWhenHidden) {
      dialog.addEventListener("sl-after-hide", (event) => {
        if (event.target !== dialog) return;
        dialog.remove();
        this.internalDialog = undefined;
        this.onDismissed?.();
      });
    }

    this.internalDialog = dialog;
    return { dialog };
  }

  hide() {
    this.internalDialog?.hide();
  }
}

export function createSimpleAlert({
  title,
  message,
  width,
  onOk,
  closeLabel,
}: {
  title: string;
  message: string | HTMLElement;
  width?: string;
  onOk?: () => void;
  closeLabel: string;
}) {
  const dialog = new IntegratedDialog<string>({
    parentNode: document.body,
    width,
    removeDialogWhenHidden: true,
    buttonsAttrs: [
      {
        value: "ok",
        label: closeLabel,
        type: "button",
        variant: "primary",
        autofocus: true,
      },
    ],
  });

  dialog.onButtonClicked = () => {
    dialog.hide();
    if (onOk) onOk();
  };

  dialog.show(title, message);
}

export interface IIntegratedDialogProps<IActions = string> {
  parentNode: HTMLElement;
  buttonsAttrs?: IButtonAttributes<IActions>[];
  preventDefaultClose?: boolean;
  preventClose?: boolean;
  removeDialogWhenHidden?: boolean;
  width?: string;
  maxWidth?: string;
  minWidth?: string;
  bodyOverflow?: string;
}

export interface IButtonAttributes<IActions = string> {
  value: IActions;
  label: string;
  type: "submit" | "button";
  outlined?: boolean;
  variant?:
    | "default"
    | "primary"
    | "success"
    | "neutral"
    | "warning"
    | "danger"
    | "text";
  icon?: string;
  autofocus?: boolean;
  disableOnClick?: boolean;
}
